import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import PixOption from "./PixOption";
import CreditCardOption from "./CreditCardOption";
import AddCardButton from "./AddCardButton";
import InstallmentsModal from "../InstallmentsModal";
import { BrlFormatCurrency } from "../../lib";
import styles from "./paymentMethods.module.css";

export default function PaymentMethods({
  cards,
  installments,
  onPaymentSelect,
  onDeleteCard,
  onAddCard,
  onInstallmentChange,
  onCardSelect,
  whitelabel,
  paymentFlags = { pix: false, card: true },
}) {
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [installmentsModalOpen, setInstallmentsModalOpen] = useState(false);

  const selectedInstallmentObj = useMemo(
    () =>
      installments?.find(
        (i) => Number(i.installmentNumber) === Number(selectedInstallment),
      ) || null,
    [installments, selectedInstallment],
  );

  const triggerLabel = selectedInstallmentObj
    ? Number(selectedInstallmentObj.installmentNumber) === 1
      ? "Pagamento à vista"
      : `${selectedInstallmentObj.installmentNumber}x de ${BrlFormatCurrency(
          selectedInstallmentObj.installmentPrice,
        )}`
    : "Em quantas parcelas deseja pagar?";

  const installmentsDisabled =
    selectedMethod !== "credit_card" || !selectedCardId;

  useEffect(() => {
    if (installmentsDisabled || !installments?.length) {
      setInstallmentsModalOpen(false);
    }
  }, [installmentsDisabled, installments]);

  const handlePixSelect = useCallback(() => {
    setSelectedMethod("pix");
    setSelectedCardId(null);
    setSelectedInstallment(null);
    onPaymentSelect({ type: "pix" });
  }, [onPaymentSelect]);

  const handleCardSelect = useCallback(
    (card) => {
      setSelectedMethod("credit_card");
      setSelectedCardId(card.uuid);
      setSelectedInstallment(null);
      onPaymentSelect({ type: "credit_card", card, installment: null });
      if (onCardSelect) {
        onCardSelect(card);
      }
    },
    [onPaymentSelect, onCardSelect],
  );

  const handleInstallmentChange = useCallback(
    (value) => {
      setSelectedInstallment(value);
      const card = cards.find((c) => c.uuid === selectedCardId);
      onPaymentSelect({ type: "credit_card", card, installment: value });
      if (onInstallmentChange) {
        onInstallmentChange(value);
      }
    },
    [cards, selectedCardId, onPaymentSelect, onInstallmentChange],
  );

  // Clear card selection if the selected card was removed from the list.
  useEffect(() => {
    if (!selectedCardId || cards.some((c) => c.uuid === selectedCardId)) {
      return;
    }
    setSelectedCardId(null);
    setSelectedInstallment(null);
    if (selectedMethod === "credit_card") {
      onPaymentSelect({ type: "credit_card", card: null, installment: null });
    }
  }, [cards, selectedCardId, selectedMethod, onPaymentSelect]);

  const lastUsedCard = cards.length > 0 ? cards[0] : null;

  return (
    <div className={styles.container}>
      <h3
        className={`${styles.sectionTitle} font-wl-600 text-mlg`}
        style={{ color: whitelabel?.theme?.text_primary_color }}
      >
        Forma de pagamento
      </h3>

      <hr className={styles.separator} />

      {/* Transferência / Pix */}
      {paymentFlags.pix !== false && (
        <>
          <p
            className={`${styles.methodTitle} font-wl-500 text-mg`}
            style={{ color: whitelabel?.theme?.text_primary_color }}
          >
            Transferência
          </p>
          <PixOption
            selected={selectedMethod === "pix"}
            onSelect={handlePixSelect}
            whitelabel={whitelabel}
          />

          <hr className={styles.separator} />
        </>
      )}

      {/* Cartão de crédito */}
      {paymentFlags.card !== false && (
        <>
          <p
            className={`${styles.methodTitle} font-wl-500 text-mg`}
            style={{ color: whitelabel?.theme?.text_primary_color }}
          >
            Cartão de crédito
          </p>
          {cards.map((card) => (
            <CreditCardOption
              key={card.uuid}
              card={card}
              selected={
                selectedMethod === "credit_card" && selectedCardId === card.uuid
              }
              highlighted={
                selectedMethod === "credit_card" && selectedCardId === card.uuid
              }
              onSelect={() => handleCardSelect(card)}
              onDelete={onDeleteCard}
              whitelabel={whitelabel}
            />
          ))}
          <AddCardButton onClick={onAddCard} whitelabel={whitelabel} />

          {/* Parcelamentos */}
          <div className={styles.installmentTriggerWrap}>
            <span className={styles.installmentLabel}>
              Parcelamentos disponíveis:
            </span>
            <button
              type="button"
              className={styles.installmentTrigger}
              onClick={() => setInstallmentsModalOpen(true)}
              disabled={
                installmentsDisabled ||
                !installments ||
                installments.length === 0
              }
            >
              <span>{triggerLabel}</span>
              <span>⌄</span>
            </button>
          </div>

          <InstallmentsModal
            open={installmentsModalOpen}
            onClose={() => setInstallmentsModalOpen(false)}
            installments={installments || []}
            selected={selectedInstallment}
            onSelect={(n) => {
              handleInstallmentChange(n);
              setInstallmentsModalOpen(false);
            }}
          />
        </>
      )}
    </div>
  );
}

PaymentMethods.propTypes = {
  cards: PropTypes.array,
  installments: PropTypes.array,
  onPaymentSelect: PropTypes.func,
  onDeleteCard: PropTypes.func,
  onAddCard: PropTypes.func,
  onInstallmentChange: PropTypes.func,
  onCardSelect: PropTypes.func,
  whitelabel: PropTypes.object,
  paymentFlags: PropTypes.shape({
    pix: PropTypes.bool,
    card: PropTypes.bool,
  }),
};
